import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Table, Tooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { track } from "statsig";
import { STATSIG } from "statsig/events";
import { IneligibleLenderCell } from "../../../MortgageCalculators/IneligibleReason";
import "../../../MortgageCalculators/IneligibleReason.css";
import "../../../MortgageCalculators/NewAllRates.css";
import {
  creditCost,
  displayProductName,
  dtiParts,
  formatMoney,
  formatPrice,
  formatRate,
  groupByTerm,
  pickTargetIndex,
  targetOf,
  termLabel,
  visibleIneligible,
  visibleProducts,
} from "./resultModel";
import { TARGET_BASIS_COST } from "../../pricingRequest";
import { ProductTags } from "./ProductTags";
import RateDetail, { detailShape } from "./RateDetail";
import AlternativeSuggestions from "./AlternativeSuggestions";
import "./PpeResults.css";

const MAX_COMPARE = 4;

// A copy of `set` with `item` added, or removed when it was there.
export function toggledSet(set, item) {
  const next = new Set(set);
  if (next.has(item)) next.delete(item);
  else next.add(item);
  return next;
}

// How Cost to borrower is worked out (11-04), the same sum as the calculator:
// calculation/costs.go (points or credit + lender fee + prepaid interest +
// fees + upfront MI + borrower-paid compensation) and reCalcLodestar.go
// (third-party fees from the closing cost service).
export const COST_TO_BORROWER_INFO = "One-time cost at closing: third-party and other fees (sections A–C and E–H) + lender fee + prepaid interest + upfront mortgage insurance + points at the price after compensation (or minus the lender credit when that price is above 100) + borrower-paid compensation. Below $0: the lender credit covers more than the costs.";

const Chevron = ({ up }) => (
  <svg className={`ppe-chevron ${up ? "ppe-chevron-up" : ""}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

// Up/down arrows: shown only on hover or focus, or on the sorted column.
const SortIcon = ({ order }) => (
  <svg className="ppe-sort-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {order !== "descend" && <path d="m7 9 5-5 5 5" />}
    {order !== "ascend" && <path d="m7 15 5 5 5-5" />}
  </svg>
);

// "23.6% / 50% max": the limit is dropped on a narrow table (CSS), the
// number stays, and the whole line is the cell's title.
function Dti({ dti, prefix }) {
  if (!dti) return null;
  const full = `${prefix}${dti.value}${dti.limit ? ` / ${dti.limit}` : ""}`;
  return (
    <span className={dti.over ? "ppe-bad" : undefined} title={full}>
      {prefix}{dti.value}{dti.limit && <span className="ppe-dti-limit"> / {dti.limit}</span>}
    </span>
  );
}

function CompCell({ row, product, compensationType }) {
  if (!product.isConnected) return <div>—</div>;
  if (compensationType === "borrower_paid") {
    return <><div>—</div><div className="ppe-sub">paid at closing</div></>;
  }
  return <><div>{formatPrice(row.comp || 0)}</div><div className="ppe-sub">{formatMoney(row.compAmount || 0)}</div></>;
}

// Mouse or focus inside the rate detail cell: not a rate row.
const inDetail = (node) => Boolean(node && node.closest && node.closest(".ppe-ladder-detail"));

// A product's rates, as rows of the same table under the same column
// headers as the product rows (user 11-04: no second header row, every rate,
// no "Show all N rates"). The Lender and Program cells beside them are one
// cell over all the rates holding a rate's whole detail (11-04: what the
// right-side drawer had): the rate under the mouse or keyboard focus, else
// the current one, which is Target until a rate is clicked (pinned). Target
// keeps its own cell before the rate.
function RateLadder({ product, current, target, onPick, hasIncome, multiLender, detail }) {
  // The rate under the mouse or focus; each product keeps its own.
  const [hovered, setHovered] = useState(null);
  // Loan costs / other costs folded open: the same for every rate.
  const [folds, setFolds] = useState({});
  const rows = product.rows;
  const targetIndex = pickTargetIndex(rows, target);
  const shown = hovered !== null && hovered < rows.length ? hovered : Math.min(current, rows.length - 1);
  // One hidden copy per detail shape keeps the cell as tall as its tallest
  // rate, so moving between rates never changes the rows' height.
  const shapes = {};
  rows.forEach((row, j) => {
    const key = detailShape(product, row, detail);
    if (!(key in shapes)) shapes[key] = j;
  });
  const toggleFold = (key) => setFolds((prev) => ({ ...prev, [key]: !prev[key] }));
  const rateDetail = (j) => (
    <RateDetail {...detail} product={product} row={rows[j]} open={folds} onToggle={toggleFold} />
  );
  const pickKey = (j) => (e) => {
    if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
    e.preventDefault();
    onPick(j);
  };
  return (
    <tbody
      className="ppe-rates"
      id={`ppe-rates-${product.id}`}
      onMouseLeave={() => setHovered(null)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setHovered(null); }}
    >
      {rows.map((row, j) => {
        const dti = dtiParts(product, row, hasIncome);
        const isTarget = j === targetIndex;
        return (
          <tr
            key={j}
            className={`ppe-rate-row ${j === current ? "ppe-rate-current" : ""} ${isTarget ? "ppe-rate-target" : ""} ${j === shown ? "ppe-rate-shown" : ""}`}
            aria-current={j === current ? "true" : undefined}
            tabIndex={0}
            onClick={(e) => { if (!inDetail(e.target)) onPick(j); }}
            onKeyDown={pickKey(j)}
            onMouseOver={(e) => { if (!inDetail(e.target)) setHovered(j); }}
            onFocus={(e) => { if (e.target === e.currentTarget) setHovered(j); }}
          >
            {j === 0 && (
              <td className="ppe-ladder-detail" rowSpan={rows.length} colSpan={multiLender ? 3 : 2}>
                <div className="ppe-ladder-detail-sticky">
                  {rateDetail(shown)}
                  {Object.keys(shapes).map((key) => (
                    <div className="ppe-ladder-detail-ghost" aria-hidden="true" key={key}>{rateDetail(shapes[key])}</div>
                  ))}
                </div>
              </td>
            )}
            <td className="ppe-ladder-lead">{isTarget && <span className="ppe-target-tag">Target</span>}</td>
            <td className="ppe-num"><span className="ppe-ladder-rate">{formatRate(row.rate)}</span></td>
            <td className="ppe-num">{formatRate(row.apr)}</td>
            <td className="ppe-num">{formatPrice(row.price)}</td>
            <td className="ppe-num">{product.isConnected && detail.compensationType !== "borrower_paid" ? formatPrice(row.comp || 0) : "—"}</td>
            <td className="ppe-num">{formatMoney(row.closing)}</td>
            <td className="ppe-num">{formatMoney(row.pmt)}</td>
            {hasIncome && <td className="ppe-num"><Dti dti={dti} prefix="" /></td>}
            <td aria-hidden="true" />
          </tr>
        );
      })}
    </tbody>
  );
}

function ReasonCell({ entry, multiTerm }) {
  const lenderGroups = entry.groups.filter((g) => g.isLender);
  const productGroups = entry.groups.filter((g) => !g.isLender);
  const terms = (g) => (multiTerm ? <span className="ppe-muted"> · {g.terms.map(termLabel).join(", ")}</span> : null);
  return (
    <div className="ineligible-reasons">
      {lenderGroups.map((g, i) => (
        <div className="ineligible-lender-problem" key={`l${i}`}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 3 }} aria-hidden="true">
            <path d="M12 3L22 20H2L12 3Z" stroke="#CB8600" strokeWidth="2" strokeLinejoin="round" />
            <path d="M12 10V14" stroke="#CB8600" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="17" r="1" fill="#CB8600" />
          </svg>
          <span>{g.reason}{terms(g)}</span>
        </div>
      ))}
      {productGroups.map((g, i) => (
        <div className="ineligible-group" key={`p${i}`}>
          <div className="ineligible-group-reason">{g.reason}{terms(g)}</div>
          <div className="ineligible-group-products">
            {g.products.map((name, j) => <span className="ineligible-product-chip" key={name + j}>{name}</span>)}
          </div>
        </div>
      ))}
    </div>
  );
}

// The pricing engine page's results (plan section 6.0, D26, D27, D29):
// the table grouped by loan term, a product opens to its rates with one
// rate's detail (price breakdown, closing cost) beside them (11-04: no eye
// buttons, no drawer).
export default function PpeResults({
  products,
  quotes,
  terms,
  lenders,
  target,
  ineligible,
  zeroText,
  alternatives,
  onTryAlternative,
  searchKey,
  searchId,
  foldedTerms: foldedTermsProp,
  onToggleGroup,
  hasIncome,
  loanAmount,
  lockDays,
  compensationType,
  appliedClosingCostsDetail,
  downPayment,
  purpose,
  statsigSource,
  onViewFeeSheet,
  onCompare,
}) {
  const [sort, setSort] = useState(null);
  const [opened, setOpened] = useState(() => new Set());
  const [closed, setClosed] = useState(() => new Set());
  const [picked, setPicked] = useState({});
  const [compare, setCompare] = useState([]);
  // Loan terms whose group is folded (11-04). Kept across a re-quote and the
  // filter bar; a new search opens every group again. The results area owns
  // it (foldedTerms / onToggleGroup), because a re-quote swaps this table for
  // the loading table and back; without those props (tests) it is kept here
  // and reset by searchId.
  const [ownFoldedTerms, setOwnFoldedTerms] = useState(() => new Set());
  const foldedTerms = foldedTermsProp || ownFoldedTerms;
  const visible = useMemo(() => visibleProducts(products, { terms, lenders }), [products, terms, lenders]);
  const noneEligible = visible.length === 0;
  const [tab, setTab] = useState(noneEligible ? "ineligible" : "eligible");

  // A new search (or re-quote) starts fresh; the tab follows whether anything is eligible.
  useEffect(() => {
    setOpened(new Set());
    setClosed(new Set());
    setPicked({});
    setCompare([]);
  }, [searchKey]);
  useEffect(() => {
    setTab(noneEligible ? "ineligible" : "eligible");
  }, [noneEligible, searchKey]);
  useEffect(() => {
    setOwnFoldedTerms(new Set());
  }, [searchId]);
  // A new target shows each product's rate at that target.
  useEffect(() => {
    setPicked({});
  }, [target]);

  const multiLender = new Set(visible.map((p) => p.lender)).size > 1;
  // A target price matched against Cost to borrower (11-04): the column tips say so.
  const byCostTarget = targetOf(target).basis === TARGET_BASIS_COST;
  const groups = useMemo(() => groupByTerm(visible, { terms, target, sort, quotes }), [visible, terms, target, sort, quotes]);
  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const currentIndex = (p) => (picked[p.id] !== undefined ? Math.min(picked[p.id], p.rows.length - 1) : pickTargetIndex(p.rows, target));
  const isOpen = (id) => opened.has(id) || (!multiLender && !closed.has(id));

  const toggleOpen = (id) => {
    const nowOpen = isOpen(id);
    setOpened((prev) => { const next = new Set(prev); nowOpen ? next.delete(id) : next.add(id); return next; });
    setClosed((prev) => { const next = new Set(prev); nowOpen ? next.add(id) : next.delete(id); return next; });
  };
  // Clicking a rate pins it: the product row and the rate detail stay on it.
  const pick = (product, index) => setPicked((prev) => ({ ...prev, [product.id]: index }));
  const toggleGroup = onToggleGroup || ((term) => setOwnFoldedTerms((prev) => toggledSet(prev, term)));
  const toggleCompare = (id) => {
    setCompare((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return prev.length >= MAX_COMPARE ? prev : [...prev, id];
    });
  };
  const changeTab = (key) => {
    if (statsigSource === "rates" && key !== tab) {
      const name = key === "ineligible" ? "ineligibles" : "eligibles";
      track(STATSIG.RATES.RESULT_TAB, name, { type: name });
    }
    setTab(key);
  };

  // Several ticked terms: a header row with the term name before each term's
  // products. One term: no header row, the products are listed directly.
  const showGroupHeads = groups.length > 1;
  // With one term and no header row, the empty table says whether that term
  // is still pricing or failed.
  const single = groups.length === 1 ? groups[0] : null;
  let emptyText = "No eligible products. The reasons are under Ineligibles.";
  if (single && single.pending) emptyText = "Pricing…";
  else if (single && single.error) emptyText = single.error;
  // Program is two columns (the second holds a rate row's Target tag).
  const ncol = 10 + (multiLender ? 1 : 0) + (hasIncome ? 1 : 0);
  const anyProducts = groups.some((g) => g.products.length > 0);

  // Ascending, descending, then back to the default order (as before).
  const clickSort = (key) => setSort((prev) => {
    if (!prev || prev.key !== key) return { key, order: "ascend" };
    return prev.order === "ascend" ? { key, order: "descend" } : null;
  });
  // `info`: a "?" beside the name with a longer explanation (Cost to
  // borrower, 11-04); the name's own tooltip stays the short one.
  const head = (key, title, tooltip, numeric, info, colSpan) => {
    const order = sort && sort.key === key ? sort.order : null;
    let ariaSort;
    if (order) ariaSort = order === "ascend" ? "ascending" : "descending";
    return (
      <th scope="col" key={key} colSpan={colSpan} className={`ppe-th-${key} ${numeric ? "ppe-num" : ""} ${order ? "ppe-sorted" : ""}`} aria-sort={ariaSort}>
        <span className="ppe-th-inner">
          <button type="button" className="ppe-sort" title={tooltip} onClick={() => clickSort(key)}>
            {title}
            <SortIcon order={order} />
          </button>
          {info && (
            <Tooltip title={info} styles={{ root: { maxWidth: 360 } }}>
              <span className="ppe-th-info" tabIndex={0} role="img" aria-label={`About ${title}`}>
                <QuestionCircleOutlined />
              </span>
            </Tooltip>
          )}
        </span>
      </th>
    );
  };

  const productRow = (p) => {
    const i = currentIndex(p);
    const row = p.rows[i];
    const open = isOpen(p.id);
    const dti = dtiParts(p, row, hasIncome);
    const price = creditCost(row.priceAmount);
    return (
      <tr
        key={p.id}
        data-row-key={p.id}
        className={`ppe-product-row ${p.isConnected ? "" : "ppe-unconnected"} ${open ? "ppe-open" : ""}`}
        onClick={() => toggleOpen(p.id)}
      >
        {multiLender && (
          <td className="ppe-check">
            <Checkbox
              checked={compare.includes(p.id)}
              disabled={!compare.includes(p.id) && compare.length >= MAX_COMPARE}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleCompare(p.id)}
              aria-label={`Compare ${p.lenderName} ${p.name}`}
            />
          </td>
        )}
        <td className="ppe-lender-cell">
          <span className="ppe-lender-name">{p.lenderName}</span>
        </td>
        <td className="ppe-product-cell" colSpan={2}>
          <div className="ppe-product-line">
            <span className="ppe-product-name" title={p.name}>{displayProductName(p.name, p.term)}</span>
            <ProductTags product={p} />
          </div>
        </td>
        <td className="ppe-num"><span className="ppe-rate-cell ppe-strong">{formatRate(row.rate)}</span></td>
        <td className="ppe-num">{formatRate(row.apr)}</td>
        <td className="ppe-num">
          <div>{formatPrice(row.price)}</div>
          <div className={`ppe-sub ${price.kind === "credit" ? "ppe-good" : ""}`}>{price.text}</div>
        </td>
        <td className="ppe-num"><CompCell row={row} product={p} compensationType={compensationType} /></td>
        <td className="ppe-num">{formatMoney(row.closing)}</td>
        <td className="ppe-num">{formatMoney(row.pmt)}</td>
        {hasIncome && <td className="ppe-num"><Dti dti={dti} prefix="" /></td>}
        <td className="ppe-num">
          <button
            type="button"
            className="ppe-link ppe-rates-toggle"
            aria-expanded={open}
            aria-controls={`ppe-rates-${p.id}`}
            aria-label={`${open ? "Hide" : "Show"} ${p.rows.length} rates`}
            onClick={(e) => { e.stopPropagation(); toggleOpen(p.id); }}
          >
            <Chevron up={open} />
          </button>
        </td>
      </tr>
    );
  };

  const bodies = [];
  groups.forEach((g) => {
    const folded = showGroupHeads && foldedTerms.has(g.term);
    const bodyId = (k) => `ppe-group-${g.term}-${k}`;
    if (showGroupHeads) {
      bodies.push(
        <tbody key={`group|${g.term}`}>
          <tr className="ppe-group-row" onClick={() => toggleGroup(g.term)}>
            <td colSpan={ncol}>
              <GroupHeader
                group={g}
                folded={folded}
                controls={g.products.map((p, k) => bodyId(k)).join(" ") || undefined}
                onToggle={(e) => { e.stopPropagation(); toggleGroup(g.term); }}
              />
            </td>
          </tr>
        </tbody>,
      );
    }
    if (folded) return;
    g.products.forEach((p, k) => {
      bodies.push(<tbody key={p.id} id={showGroupHeads ? bodyId(k) : undefined}>{productRow(p)}</tbody>);
      if (isOpen(p.id)) {
        bodies.push(
          <RateLadder
            key={`rates|${p.id}`}
            product={p}
            current={currentIndex(p)}
            target={target}
            onPick={(j) => pick(p, j)}
            hasIncome={hasIncome}
            multiLender={multiLender}
            detail={{
              loanAmount,
              lockDays,
              compensationType,
              appliedClosingCostsDetail,
              downPayment,
              purpose,
              onViewFeeSheet: (row) => onViewFeeSheet(row.raw),
            }}
          />,
        );
      }
    });
  });

  const multiTerm = terms.length > 1;
  const shownIneligible = useMemo(() => visibleIneligible(ineligible, { terms, lenders, products }), [ineligible, terms, lenders, products]);

  return (
    <div className="ppe-results">
      {zeroText && noneEligible && (
        <div className="ppe-no-eligible-notice" role="status">
          {zeroText}
          <AlternativeSuggestions alternatives={alternatives} onTry={onTryAlternative} />
        </div>
      )}
      <div className="ppe-tabs-row">
        <div className="ppe-tabs" role="tablist">
          {[["eligible", "Eligibles", visible.length], ["ineligible", "Ineligibles", shownIneligible.length]].map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`ppe-tab ${tab === key ? "active" : ""}`}
              onClick={() => changeTab(key)}
            >
              {label}
              <span className="ppe-tab-count">{count}</span>
            </button>
          ))}
        </div>
        {multiLender && tab === "eligible" && (
          <div className="ppe-compare-bar">
            <span className="ppe-selected-count">{compare.length} selected</span>
            {compare.length > 0 && <button type="button" className="ppe-btn ppe-btn-text" onClick={() => setCompare([])}>Clear</button>}
            <button
              type="button"
              className="ppe-btn ppe-btn-outline"
              disabled={compare.length < 2}
              onClick={() => onCompare(compare.map((id) => {
                const p = byId[id];
                return { product: p, row: p.rows[currentIndex(p)] };
              }))}
            >
              Compare
            </button>
          </div>
        )}
      </div>
      {tab === "ineligible" ? (
        <Table
          className="ineligibletable rates-page-table full-table rates-page-table-new"
          pagination={false}
          rowKey="Lender"
          dataSource={shownIneligible}
          rowClassName="ineligibleRow"
          scroll={{ x: "max-content" }}
          locale={{ emptyText: <div style={{ color: "#6E6E70" }}>No ineligible products</div> }}
          columns={[
            { title: "Lender", key: "lender", width: 250, className: "ineligibleColumn", render: (_, entry) => <IneligibleLenderCell record={{ Lender: entry.Lender, ProductLevelReasons: Array(entry.productCount).fill(null) }} /> },
            { title: "Ineligible reason", key: "reason", className: "ineligibleColumn", render: (_, entry) => <ReasonCell entry={entry} multiTerm={multiTerm} /> },
          ]}
        />
      ) : (
        <div className="ppe-table-scroll">
          <table className={`ppe-results-table ${hasIncome ? "ppe-has-dti" : ""}`}>
            <colgroup>
              {multiLender && <col className="ppe-col-check" />}
              <col className="ppe-col-lender" />
              <col className="ppe-col-product" />
              <col className="ppe-col-lead" />
              <col className="ppe-col-rate" />
              <col className="ppe-col-apr" />
              <col className="ppe-col-price" />
              <col className="ppe-col-comp" />
              <col className="ppe-col-closing" />
              <col className="ppe-col-pmt" />
              {hasIncome && <col className="ppe-col-dti" />}
              <col className="ppe-col-last" />
            </colgroup>
            <thead>
              <tr>
                {multiLender && <th scope="col"><span className="ppe-sr-only">Compare</span></th>}
                {head("lender", "Lender")}
                {head("program", "Program", undefined, false, undefined, 2)}
                {head("rate", "Rate", "Interest rate", true)}
                {head("apr", "APR", "Annual percentage rate", true)}
                {head("price", "Price", byCostTarget
                  ? "Lock-day price plus the pricing adjustments, before compensation."
                  : "Lock-day price plus the pricing adjustments, before compensation: the number the target price is matched against.", true)}
                {head("comp", "Comp.", "Lender-paid compensation set on the Lenders page.", true)}
                {head("closing", "Cost to borrower", byCostTarget
                  ? "Estimated one-time cost at closing. The target price is matched against it as 100 − cost ÷ loan amount × 100."
                  : "Estimated one-time cost at closing", true, COST_TO_BORROWER_INFO)}
                {head("pmt", "PMT", "Monthly principal and interest payment.", true)}
                {hasIncome && head("dti", "DTI", "Debt-to-income ratio at this rate, and the program's maximum.", true)}
                <th scope="col"><span className="ppe-sr-only">Rates</span></th>
              </tr>
            </thead>
            {bodies}
            {!anyProducts && !showGroupHeads && (
              <tbody>
                <tr className="ppe-empty-row">
                  <td colSpan={ncol}>
                    <div className={`ppe-muted ${single && single.error ? "ppe-bad" : ""}`} role={single && single.pending ? "status" : undefined}>{emptyText}</div>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// The term name, with an arrow that folds or opens the term's products
// (11-04; only shown when several terms are ticked). A term still pricing,
// failed or with nothing eligible says so beside the name, since no product
// rows follow it; a folded term says how many products it hides.
function GroupHeader({ group, folded, controls, onToggle }) {
  let detail = null;
  if (group.pending) detail = "Pricing…";
  else if (group.error) detail = group.error;
  else if (group.products.length === 0) detail = "No eligible products";
  else if (folded) detail = `${group.products.length} ${group.products.length === 1 ? "product" : "products"}`;
  return (
    <div className="ppe-group-head">
      <button
        type="button"
        className="ppe-link ppe-group-toggle"
        aria-expanded={!folded}
        aria-controls={controls}
        aria-label={`${folded ? "Show" : "Hide"} ${group.label} products`}
        onClick={onToggle}
      >
        <Chevron up={!folded} />
      </button>
      <span className="ppe-group-label">{group.label}</span>
      {detail && <span className={`ppe-muted ${group.error ? "ppe-bad" : ""}`} role={group.pending ? "status" : undefined}>{detail}</span>}
    </div>
  );
}
